import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { AlignBottomIcon } from '@/components/tiptap-editor/tiptap-icons/align-bottom-icon';
import { AlignTopIcon } from '@/components/tiptap-editor/tiptap-icons/align-top-icon';
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { moveNode } from '@/hooks/use-move-node';
import { useSiblingNodeInfo } from '@/hooks/use-sibling-node-info';
import type { Editor } from '@tiptap/core';
import { type Node as PMNode } from '@tiptap/pm/model';
import { type ReactNodeViewProps, NodeViewContent, useEditorState } from '@tiptap/react';
import { CopyPlusIcon, HighlighterIcon, Trash2Icon } from 'lucide-react';
import { memo, useCallback } from 'react';

function getDetectAnswerContext(
    editor: ReactNodeViewProps['editor'],
    questionPos: number,
): {
    detectAnswerPos: number;
    detectAnswerNode: PMNode;
} | null {
    const $pos = editor.state.doc.resolve(questionPos);

    for (let depth = $pos.depth; depth >= 0; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === 'detectAnswer') {
            return {
                detectAnswerPos: $pos.before(depth),
                detectAnswerNode: node,
            };
        }
    }

    return null;
}

export function canToggleDetectAnswerOption(editor: Editor, getPos: () => number | undefined): boolean {
    const questionPos = getPos();
    if (typeof questionPos !== 'number' || questionPos < 0) {
        return false;
    }

    // hideDiff restores the shorter document before ProseMirror drops diff-view
    // descs, so getPos() can still point past the restored document.
    if (questionPos > editor.state.doc.content.size) {
        return false;
    }

    const questionNode = editor.state.doc.nodeAt(questionPos);
    if (!questionNode || questionNode.type.name !== 'detectAnswerQuestion') {
        return false;
    }

    const { from, to } = editor.state.selection;
    if (from === to) {
        return false;
    }

    const questionEnd = questionPos + questionNode.nodeSize;
    if (from < questionPos || to > questionEnd) {
        return false;
    }

    return editor.state.doc.textBetween(from, to, '\n').trim().length > 0;
}

function detectAnswerHasCorrectOption(editor: Editor, getPos: () => number | undefined): boolean {
    try {
        const questionPos = getPos();
        const docSize = editor.state?.doc?.content?.size;
        if (typeof questionPos !== 'number' || docSize == null || questionPos > docSize) {
            return true;
        }

        const detectAnswerContext = getDetectAnswerContext(editor, questionPos);
        if (!detectAnswerContext) {
            return true;
        }

        let hasOptions = false;
        let hasCorrect = false;
        detectAnswerContext.detectAnswerNode.forEach(child => {
            if (child.type.name !== 'detectAnswerOption') {
                return;
            }
            hasOptions = true;
            if (child.attrs.isCorrect) {
                hasCorrect = true;
            }
        });

        if (!hasOptions) {
            return true;
        }

        return hasCorrect;
    } catch {
        return true;
    }
}

const DetectAnswerQuestionNodeView: React.FC<ReactNodeViewProps> = ({ editor, getPos, node, selected, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const getDetectAnswerPos = useCallback(() => {
        const questionPos = getPos();
        if (typeof questionPos !== 'number') {
            return undefined;
        }

        return getDetectAnswerContext(editor, questionPos)?.detectAnswerPos;
    }, [editor, getPos]);

    const { nodeIndex, totalNodes } = useSiblingNodeInfo({
        editor,
        getPos: getDetectAnswerPos,
        nodeTypeName: 'detectAnswer',
    });

    const canToggleAnswerOption = useEditorState({
        editor,
        selector: ({ editor: ed }) => (ed ? canToggleDetectAnswerOption(ed, getPos) : false),
    });

    const hasCorrectOption = useEditorState({
        editor,
        selector: ({ editor: ed }) => (ed ? detectAnswerHasCorrectOption(ed, getPos) : true),
    });

    const showError = !hasCorrectOption;
    const errorId = `da-no-correct-answer-${node.attrs.id ?? getPos()}`;

    const handleMoveUp = () => {
        if (nodeIndex === 0) return;

        const questionPos = getPos();
        if (typeof questionPos !== 'number') return;

        const detectAnswerContext = getDetectAnswerContext(editor, questionPos);
        if (!detectAnswerContext) return;

        moveNode(editor, 'up', {
            pos: detectAnswerContext.detectAnswerPos,
            node: detectAnswerContext.detectAnswerNode,
        });
    };

    const handleMoveDown = () => {
        const questionPos = getPos();
        if (typeof questionPos !== 'number') return;

        const detectAnswerContext = getDetectAnswerContext(editor, questionPos);
        if (!detectAnswerContext) return;

        moveNode(editor, 'down', {
            pos: detectAnswerContext.detectAnswerPos,
            node: detectAnswerContext.detectAnswerNode,
        });
    };

    const handleDelete = () => {
        if (totalNodes <= 1) return;

        if (window.confirm('Are you sure you want to delete this question?')) {
            const questionPos = getPos();
            if (typeof questionPos !== 'number') return;

            const detectAnswerContext = getDetectAnswerContext(editor, questionPos);
            if (!detectAnswerContext) return;

            editor
                .chain()
                .deleteRange({
                    from: detectAnswerContext.detectAnswerPos,
                    to: detectAnswerContext.detectAnswerPos + detectAnswerContext.detectAnswerNode.nodeSize,
                })
                .run();
        }
    };

    const handleToggle = useCallback(() => {
        const questionPos = getPos();
        if (typeof questionPos !== 'number') {
            return;
        }

        editor.commands.toggleDetectAnswerOptionFromSelection(questionPos);
    }, [editor, getPos]);

    const handleMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        // Keep the current text selection in the editor so the command can read it.
        event.preventDefault();
    }, []);

    const handleDuplicate = () => {
        if (window.confirm('Are you sure you want to duplicate this question?')) {
            const questionPos = getPos();
            if (typeof questionPos !== 'number') return;

            const detectAnswerContext = getDetectAnswerContext(editor, questionPos);
            if (!detectAnswerContext) return;

            editor
                .chain()
                .duplicateNode(
                    detectAnswerContext.detectAnswerNode,
                    detectAnswerContext.detectAnswerPos + detectAnswerContext.detectAnswerNode.nodeSize,
                )
                .scrollIntoView()
                .run();
        }
    };

    const isDeleteDisabled = totalNodes <= 1;
    const isMoveUpDisabled = nodeIndex === 0 || totalNodes <= 1;
    const isMoveDownDisabled = nodeIndex === totalNodes - 1 || totalNodes <= 1;

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={`Question ${nodeIndex + 1}`}
            selected={selected}
            containerClassName="rounded-lg border-2 border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/50"
            contentClassName="pt-3 px-1"
        >
            <div className="flex gap-1">
                <div contentEditable={false}>
                    <div className="flex flex-col gap-2 ">
                        <Button
                            onClick={handleMoveUp}
                            data-style="ghost"
                            aria-label="Move question up"
                            disabled={isMoveUpDisabled || isEditorReadonly}
                            tooltip={isMoveUpDisabled ? 'This is the first question in the question set' : 'Move up'}
                        >
                            <AlignTopIcon className="h-4 w-4" />
                        </Button>
                        <Button
                            onClick={handleMoveDown}
                            data-style="ghost"
                            aria-label="Move question down"
                            disabled={isMoveDownDisabled || isEditorReadonly}
                            tooltip={isMoveDownDisabled ? 'This is the last question in the question set' : 'Move down'}
                        >
                            <AlignBottomIcon className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
                <div className="w-full h-full">
                    <NodeViewContent />
                    <span
                        id={errorId}
                        className={`text-xs text-red-600 dark:text-red-400${showError ? '' : ' hidden'}`}
                        aria-hidden={!showError}
                    >
                        An answer must be marked as correct
                    </span>
                </div>
                <div
                    className="flex flex-col"
                    contentEditable={false}
                >
                    <Button
                        onClick={handleDelete}
                        data-style="ghost"
                        aria-label="Delete question"
                        disabled={isDeleteDisabled || isEditorReadonly}
                        tooltip={isDeleteDisabled ? 'A question set must have at least one question' : 'Delete question'}
                    >
                        <Trash2Icon className="h-4 w-4" />
                    </Button>
                    <Button
                        onClick={handleDuplicate}
                        data-style="ghost"
                        aria-label="Duplicate question"
                        disabled={isEditorReadonly}
                        tooltip="Duplicate question"
                    >
                        <CopyPlusIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        onMouseDown={handleMouseDown}
                        onClick={handleToggle}
                        data-style="ghost"
                        aria-label="Toggle answer option"
                        disabled={!canToggleAnswerOption || isEditorReadonly}
                        tooltip={canToggleAnswerOption ? 'Toggle answer option for selected text' : 'Select text to toggle answer option'}
                    >
                        <HighlighterIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </CommonNodeViewWrapper>
    );
};

export default memo(DetectAnswerQuestionNodeView);
